import { isAfter, startOfDay } from "date-fns";
import { useTranslation } from "react-i18next";
import { Divider, Form, Grid, GridColumn, Icon, Modal } from "semantic-ui-react";

import { createFormHelper } from "lib/Form";

import type { ParkingGateCode } from "models";
import type { AddUpdateParkingGateCodeFormValues } from "modules/ParkingGateCodesDashboard/components/AddUpdateParkingGateCodeModal";

type AddUpdateParkingGateCodeFormProps = {
  hasErrors: boolean;
  isPending: boolean;
  parkingGateCode: ParkingGateCode | null;
  onClose: () => void;
};

const FormHelper = createFormHelper<AddUpdateParkingGateCodeFormValues>();

export const AddUpdateParkingGateCodeForm = ({ hasErrors, isPending, onClose, parkingGateCode }: AddUpdateParkingGateCodeFormProps) => {
  const { t } = useTranslation();
  const { watch, setValue } = FormHelper.useFormContext();
  const [pin, startAt, endAat] = watch(["pin", "start_at", "end_at"]);

  const handleDateChange = (date: Date | null) => date && date > new Date(endAat) && setValue("end_at", "");

  return (
    <>
      <Modal.Header className="padding-container">
        <div className="header-title">
          {!parkingGateCode ? t("v8_add_parking_gate_code", "Add Parking Gate Code") : t("v8_edit_parking_gate_code", "Edit Parking Gate Code")}
        </div>
      </Modal.Header>
      <Divider fitted />
      <Modal.Content className="padding-container">
        <Grid columns="equal">
          <GridColumn width={8}>
            <Form.Field required error={hasErrors && (!startAt || (startAt && endAat && isAfter(startOfDay(startAt), startOfDay(endAat))))}>
              <label>{t("v8_start_at", "Start At")} </label>
              <FormHelper.Field.DatePicker name="start_at" fluid minDate={new Date()} onChange={handleDateChange} />
            </Form.Field>
          </GridColumn>
          <GridColumn width={8}>
            <Form.Field required error={!endAat && hasErrors}>
              <label>{t("v8_end_at", "End At")} </label>
              <FormHelper.Field.DatePicker name="end_at" fluid minDate={new Date(startAt)} />
            </Form.Field>
          </GridColumn>
        </Grid>
        <Grid columns="equal">
          <GridColumn>
            <Form.Field required error={!pin && hasErrors}>
              <label>{t("v8_pin", "PIN").toUpperCase()} </label>
              <FormHelper.Field.Input name="pin" inputProps={{ placeholder: t("v8_pin", "Pin"), fluid: true }} />
            </Form.Field>
          </GridColumn>
        </Grid>
      </Modal.Content>
      <Divider fitted />
      <Modal.Actions className="padding-container">
        <div className="actions-buttons-main-container">
          <div className="save-cancel-buttons-container">
            <Form.Button className="light" onClick={onClose}>
              {t("v8_cancel", "Cancel")}
              <Icon className="xmark" />
            </Form.Button>
            <Form.Button type="submit" color="green" disabled={isPending} loading={isPending}>
              {!parkingGateCode ? t("v8_save", "Save") : t("v8_update", "Update")}
              <Icon className="check" />
            </Form.Button>
          </div>
        </div>
      </Modal.Actions>
    </>
  );
};
